/**
 * Гілка коментарів (див. backend/src/api/thread): під серією, під аніме,
 * загальний чат сайту й під кліпом.
 *
 * Формат той самий, що в чату ефіру — ніки, кольори, значки, смайли, до
 * пʼяти картинок, відповіді, модерація, — але транспорт звичайний HTTP:
 * коментарі не біжать щосекунди, і тримати на кожен блок сторінки (їх на
 * сторінці серії буває три) окремий вебсокет немає сенсу. Нове підтягуємо
 * опитуванням, своє показуємо одразу.
 *
 * Старі коментарі (comments.app і найперші твічеві) бекенд переносить у гілку
 * сам, з першим же читанням — тут вони приходять звичайними повідомленнями з
 * позначкою legacy.
 */

const DEFAULT_LIMIT = 30
const DEFAULT_INTERVAL = 10000

const byTime = (a, b) => {
  const diff = new Date(a.at).getTime() - new Date(b.at).getTime()
  return diff || Number(a.id) - Number(b.id)
}

export const useThread = (room, options = {}) => {
  const { limit = DEFAULT_LIMIT, interval = DEFAULT_INTERVAL } = options

  const config = useRuntimeConfig()
  const token = useStrapiToken()
  const apiBase = computed(() => (config.public.apiBase || '').replace(/\/+$/, ''))

  const messages = ref([])
  const total = ref(0)
  const hasMore = ref(false)
  const loading = ref(true)
  const loadingMore = ref(false)
  const failed = ref(false)
  // Профіль очима чату: імʼя, колір, модератор, мовчанка. null — не
  // залогінений (читати можна, писати — ні)
  const me = ref(null)
  const limits = ref({ maxText: 2000, maxMedia: 5, maxMediaBytes: 10 * 1024 * 1024 })
  const notice = ref('')

  const canModerate = computed(() => !!me.value?.moderator)
  const muted = computed(() => {
    const until = me.value?.mutedUntil
    return !!until && new Date(until).getTime() > Date.now()
  })

  const headers = () => (token.value ? { Authorization: `Bearer ${token.value}` } : {})

  const endpoint = (path) => `${apiBase.value}/api/threads${path}`

  const errorText = (e, fallback) => e?.data?.error?.message || fallback

  /**
   * Вливає пачку в стрічку: нові додаються, знайомі оновлюються (оцінки,
   * «видалено»), а вже догорнуте старе лишається на місці
   */
  const merge = (incoming) => {
    const map = new Map(messages.value.map((m) => [String(m.id), m]))
    for (const message of incoming) map.set(String(message.id), message)
    messages.value = [...map.values()].sort(byTime)
  }

  let inFlight = false
  let generation = 0

  const fetchPage = (offset, take) =>
    $fetch(endpoint(`/room/${encodeURIComponent(toValue(room))}`), {
      query: { offset, limit: take },
      headers: headers()
    })

  /** Найсвіжіша сторінка — і на старті, і в кожному опитуванні */
  const load = async () => {
    const id = toValue(room)
    if (!import.meta.client || !id || inFlight) return

    inFlight = true
    const mine = generation
    try {
      const data = await fetchPage(0, limit)
      // Поки йшов запит, гілку могли перемкнути
      if (mine !== generation) return

      // «Є старіші» вирішує тільки перша сторінка: опитування тягне найсвіжіші,
      // а догорнуте вглиб рахує loadMore
      const first = !messages.value.length
      merge(data.messages || [])
      total.value = data.total || 0
      if (first) hasMore.value = !!data.hasMore
      me.value = data.me || null
      if (data.limits) limits.value = { ...limits.value, ...data.limits }
      failed.value = false
    } catch (e) {
      if (mine === generation) failed.value = true
    } finally {
      inFlight = false
      if (mine === generation) loading.value = false
    }
  }

  /** Догорнули до верху — старіша сторінка */
  const loadMore = async () => {
    if (!hasMore.value || loadingMore.value) return
    loadingMore.value = true
    const mine = generation
    try {
      // Зсув рахуємо від кількості вже показаних: нові коментарі за цей час
      // могли зсунути сторінки, але дублікати зшиваються за id
      const data = await fetchPage(messages.value.length, limit)
      if (mine !== generation) return
      merge(data.messages || [])
      hasMore.value = !!data.hasMore
    } catch (e) {
      notice.value = 'Не вдалося завантажити старіші'
    } finally {
      loadingMore.value = false
    }
  }

  /**
   * Новий коментар. Помилку кидаємо з причиною — композер покаже її й
   * лишить набране в полі
   */
  const send = async ({ text, mediaIds = [], spoiler = false, replyTo = null }) => {
    try {
      const { message } = await $fetch(endpoint(`/room/${encodeURIComponent(toValue(room))}`), {
        method: 'POST',
        headers: headers(),
        body: { text, mediaIds, spoiler, replyTo: replyTo?.id || null }
      })
      merge([message])
      total.value += 1
      return true
    } catch (e) {
      throw new Error(errorText(e, 'Не вдалося надіслати коментар'))
    }
  }

  const remove = async (id) => {
    try {
      const { message } = await $fetch(endpoint(`/messages/${id}`), {
        method: 'DELETE',
        headers: headers()
      })
      merge([{ ...message, mine: 0 }])
    } catch (e) {
      notice.value = errorText(e, 'Не вдалося видалити')
    }
  }

  /**
   * Оцінка: показуємо одразу, сервер підтверджує рахунок
   *
   * @param {number} value 1 — подобається, -1 — ні; повторне натискання
   *   того самого знімає оцінку
   */
  const react = async (message, value) => {
    if (!me.value) {
      notice.value = 'Увійдіть, щоб оцінювати'
      return
    }
    const next = message.mine === value ? 0 : value
    const before = { likes: message.likes, dislikes: message.dislikes, mine: message.mine }

    const optimistic = { ...message, mine: next }
    if (before.mine === 1) optimistic.likes -= 1
    if (before.mine === -1) optimistic.dislikes -= 1
    if (next === 1) optimistic.likes += 1
    if (next === -1) optimistic.dislikes += 1
    merge([optimistic])

    try {
      const result = await $fetch(endpoint(`/messages/${message.id}/react`), {
        method: 'POST',
        headers: headers(),
        body: { value: next }
      })
      merge([{ ...optimistic, ...result }])
    } catch (e) {
      merge([{ ...message, ...before }])
      notice.value = errorText(e, 'Не вдалося оцінити')
    }
  }

  const mute = async (userId, seconds) => {
    try {
      await $fetch(endpoint('/mute'), {
        method: 'POST',
        headers: headers(),
        body: { userId, seconds }
      })
      notice.value = seconds ? 'Глядачеві заборонено писати' : 'Мовчанку знято'
    } catch (e) {
      notice.value = errorText(e, 'Не вдалося')
    }
  }

  const uploadMedia = (file) =>
    uploadChatMedia(file, {
      apiBase: apiBase.value,
      token: token.value,
      maxBytes: limits.value.maxMediaBytes
    })

  let timer = null

  const start = () => {
    if (!import.meta.client || timer) return
    load()
    timer = setInterval(() => {
      // У фоновій вкладці оновлювати нема кому
      if (!document.hidden) load()
    }, interval)
  }

  const stop = () => {
    if (timer) clearInterval(timer)
    timer = null
  }

  watch(
    () => toValue(room),
    () => {
      generation += 1
      inFlight = false
      messages.value = []
      total.value = 0
      hasMore.value = false
      loading.value = true
      load()
    }
  )

  // Увійшли/вийшли в іншій частині сторінки — перечитуємо, щоб зʼявилось
  // поле написання й свої оцінки
  watch(token, () => load())

  onMounted(start)
  onBeforeUnmount(stop)

  return {
    messages,
    total,
    hasMore,
    loading,
    loadingMore,
    failed,
    me,
    limits,
    notice,
    canModerate,
    muted,
    load,
    loadMore,
    send,
    remove,
    react,
    mute,
    uploadMedia
  }
}
